import { forwardRef } from 'react';

import { scheduleTimeGridStyles } from './schedule-time-grid-item.components';

import { Skeleton } from '@/components/ui/skeleton.component';
import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { useGridCol } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-grid-col.hook';
import { useGridRows } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-grid-rows.hook';

interface SingleColLoadingProps {
  scheduleType: ScheduleType;
  size?: number;
}

type PossibleScheduleType = ScheduleType.SCHEDULED | ScheduleType.ARRIVAL_ORDER | ScheduleType.LABORATORIAL;

export const SingleColLoading = forwardRef<HTMLDivElement, SingleColLoadingProps>(({ scheduleType, size }, ref) => {
  const { container, slot } = scheduleTimeGridStyles();

  const scheduleRows = useGridRows();

  const rows = scheduleType === ScheduleType.ARRIVAL_ORDER || ScheduleType.LABORATORIAL ? 1 : scheduleRows;

  return (
    <div className={container({ scheduleType: scheduleType as PossibleScheduleType })} ref={ref}>
      <div className="flex flex-col items-center gap-2 sticky top-0 bg-white w-full h-11 z-10 -mb-1 mt-1">
        <Skeleton className="w-10 h-4" />
        <Skeleton className="w-10 h-3" />
      </div>
      {Array.from({ length: size ?? rows }).map((_, colIndex) => (
        <Skeleton key={`key-col-hour-skeleton-${colIndex}`} className={slot({ scheduleType: scheduleType as PossibleScheduleType })} />
      ))}
    </div>
  );
});

SingleColLoading.displayName = 'SingleColLoading';

export default function TimeGridLoading({ scheduleType, size }: SingleColLoadingProps) {
  const cols = useGridCol({ scheduleType });

  return (
    <div className="h-full w-full justify-between flex flex-col gap-4">
      <div className="h-full w-full flex justify-center min-w-0 shrink-0 grow-0">
        <Skeleton className="h-7 w-7 rounded-full mt-1 -mr-2 z-20" />
        <div className="w-[260px] sm:w-[calc(100%-85px)] flex min-w-0 shrink-0 grow-0">
          {Array.from({ length: cols }).map((_, rowIndex) => (
            <SingleColLoading key={`key-day-skeleton-${rowIndex}`} scheduleType={scheduleType} size={size} />
          ))}
        </div>
        <Skeleton className="h-7 w-7 rounded-full mt-1 -ml-2 z-20" />
      </div>
      {scheduleType === ScheduleType.SCHEDULED && <Skeleton className="w-full h-6" />}
    </div>
  );
}
